import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { ErrorBoundary } from '@/app/error-boundary';
import { AppProviders } from '@/app/providers/app-providers';
import { practiceMemberOptions, preferencesOptions } from '@/entities/practice-member';
import { notesSettingsOptions } from '@/entities/practice-member-settings';
import { isCheckoutSettling, subscriptionMeOptions } from '@/entities/subscription';
import {
    emailVerificationStatusOptions,
    resolveEmailVerificationGate,
    sessionReadyOptions,
} from '@/entities/user';
import { redirectToSignIn } from '@/shared/api/redirect-to-sign-in';
import {
    grantEmailVerificationGateExemption,
    hasEmailVerificationGateExemption,
} from '@/shared/lib/email-verification-gate-exemption';
import { useWebSocketClient } from '@/shared/lib/use-web-socket-client';

function RootComponent() {
    useWebSocketClient();

    return (
        <AppProviders>
            <Outlet />
        </AppProviders>
    );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
    component: RootComponent,
    errorComponent: ErrorBoundary,
    validateSearch: z.object({
        finalizing: z.coerce.boolean().optional(),
        checkout_session_id: z.string().optional(),
    }),
    beforeLoad: async ({ context: { queryClient }, location, search }) => {
        if (search.finalizing) {
            try {
                await queryClient.ensureQueryData(sessionReadyOptions());
            } catch {
                redirectToSignIn(false);
                await new Promise(() => {});
            }
        }

        const [preferences, subscription] = await Promise.all([
            queryClient.ensureQueryData(preferencesOptions()),
            queryClient.ensureQueryData(subscriptionMeOptions()),
            queryClient.ensureQueryData(practiceMemberOptions()),
            // Onboarding and recording read this without an absence guard, so it must be cached first.
            queryClient.ensureQueryData(notesSettingsOptions()),
        ]);
        const hasActiveOnboarding = !!preferences?.onboarding?.step;
        const isOnSignUp = location.pathname === '/sign-up';
        const isOnTrialExpired = location.pathname === '/trial-expired';
        const isOnEmailUnverified = location.pathname === '/email-unverified';

        if (hasActiveOnboarding && !isOnSignUp) {
            throw redirect({ to: '/sign-up', replace: true });
        }
        if (!hasActiveOnboarding && isOnSignUp) {
            throw redirect({ to: '/dashboard', replace: true });
        }

        const isSettling = isCheckoutSettling(subscription, search.checkout_session_id);

        if (!isOnSignUp && !isSettling && subscription?.isTrial) {
            const isExpiredTrial = subscription.status === 'CANCELED';
            if (isExpiredTrial && !isOnTrialExpired) {
                throw redirect({ to: '/trial-expired', replace: true });
            }
            if (!isExpiredTrial && isOnTrialExpired) {
                throw redirect({ to: '/dashboard', replace: true });
            }
        }

        // Trial exemption: granted while still on the trial and never revoked, so an upgrade
        // mid-session keeps working; a later sign-in is already paid, so it is never granted again
        // and the gate bites. Paid users get the same exemption elsewhere, on a successful email
        // change (see use-email-change.ts).
        if (subscription?.isTrial) {
            grantEmailVerificationGateExemption();
        }

        const isPaidPlan = !!subscription && !subscription.isTrial;
        const isExempt = isOnSignUp || hasEmailVerificationGateExemption();
        // ensureQueryData would serve a cached "unverified" without honoring staleTime.
        const isEmailVerified =
            !isPaidPlan ||
            isExempt ||
            (await queryClient.fetchQuery(emailVerificationStatusOptions())).verified;

        const gateRedirect = resolveEmailVerificationGate({
            isPaidPlan,
            isEmailVerified,
            isOnEmailUnverified,
            isExempt,
        });
        if (gateRedirect) {
            throw redirect({ to: gateRedirect, replace: true });
        }
    },
});
